import { useEffect, useRef, useState } from 'react';
import type { PickCallbacks } from './kit';

export function useScene<H extends { dispose(): void }>(
  name: string,
  load: (canvas: HTMLCanvasElement, cb: PickCallbacks) => Promise<H>,
  cb: Omit<PickCallbacks, 'onLost'>,
  onReady: (h: H) => void,
) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const handleRef = useRef<H | null>(null);
  const [failed, setFailed] = useState(false);
  const live = useRef({ load, cb, onReady });
  live.current = { load, cb, onReady };

  useEffect(() => {
    const placeholder = canvasRef.current;
    if (!placeholder || failed) return;
    let cancelled = false;
    const root = document.documentElement;
    const { canvas, restore } = freshCanvas(placeholder);
    live.current
      .load(canvas, {
        onPick: (id) => live.current.cb.onPick(id),
        onHover: (id) => live.current.cb.onHover(id),
        onLost: () => { if (!cancelled) setFailed(true); },
      })
      .then((h) => {
        if (cancelled) { h.dispose(); return; }
        handleRef.current = h;
        live.current.onReady(h);
        root.setAttribute('data-scene', name);
      })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => {
      cancelled = true;
      handleRef.current?.dispose();
      handleRef.current = null;
      restore();
      root.removeAttribute('data-scene');
    };
  }, [failed, name]);

  return { canvasRef, handleRef, failed };
}

export function useFollow(
  ref: React.RefObject<HTMLElement | null>,
  locate: () => { x: number; y: number } | null,
  active: boolean,
) {
  const live = useRef(locate);
  live.current = locate;
  useEffect(() => {
    if (!active) return;
    let raf = 0;
    const tick = () => {
      const el = ref.current;
      const at = live.current();
      if (el) {
        if (at) {
          el.style.transform = `translate(${Math.round(at.x)}px, ${Math.round(at.y)}px)`;
          el.style.visibility = 'visible';
        } else el.style.visibility = 'hidden';
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active, ref]);
}

export function useInsets(
  handleRef: React.MutableRefObject<{ setInsets(top: number, bottom: number): void } | null>,
  topSelectors: string[],
  bottomSelectors: string[],
) {
  const apply = useRef<() => void>(() => {});
  useEffect(() => {
    const els = () => [...topSelectors, ...bottomSelectors].map((s) => document.querySelector<HTMLElement>(s)).filter((e): e is HTMLElement => !!e);
    const run = () => {
      const top = Math.max(0, ...topSelectors.map((s) => document.querySelector(s)?.getBoundingClientRect().bottom ?? 0));
      const bottom = Math.max(0, ...bottomSelectors.map((s) => {
        const r = document.querySelector(s)?.getBoundingClientRect();
        return r && r.height > 0 ? innerHeight - r.top : 0;
      }));
      handleRef.current?.setInsets(top, bottom);
    };
    apply.current = run;
    const ro = new ResizeObserver(run);
    els().forEach((e) => ro.observe(e));
    window.addEventListener('resize', run);
    run();
    return () => { ro.disconnect(); window.removeEventListener('resize', run); };
  }, [topSelectors.join(), bottomSelectors.join()]);
  return apply;
}

export function freshCanvas(placeholder: HTMLCanvasElement): { canvas: HTMLCanvasElement; restore(): void } {
  const canvas = document.createElement('canvas');
  canvas.className = placeholder.className;
  canvas.setAttribute('aria-hidden', 'true');
  placeholder.after(canvas);
  placeholder.style.display = 'none';
  return {
    canvas,
    restore() {
      canvas.remove();
      placeholder.style.display = '';
    },
  };
}
